import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { prefijoDocenteFromCurp, cargoConGeneroFromCurp } from "@/lib/curp";

const APP_LOGO = "https://customer-assets-rejwkqb3.emergentagent.net/job_edu-censo-admin/artifacts/8c23fucn_Logo%20Censo911.png";
const MESES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
const LUGAR_TXT = { 1: "PRIMER LUGAR", 2: "SEGUNDO LUGAR", 3: "TERCER LUGAR" };
const PERIODO_TXT = { 1: "PRIMER", 2: "SEGUNDO", 3: "TERCER" };
const nombreCompleto = (a) => [a.nombre, a.apellido_paterno, a.apellido_materno].filter(Boolean).join(" ");
const titleCase = (s) => (s || "").toLowerCase().split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

// Paleta
const BURGUNDY = "#7A1E37";
const BURGUNDY_DARK = "#5C1428";
const BURGUNDY_LIGHT = "#A63A54";
const GOLD = "#C9A24A";
const GOLD_DARK = "#8F6B1F";

// Paleta B/N — se sustituye por escala de grises
const BW = {
  burgundy: "#000000",
  burgundyDark: "#000000",
  burgundyLight: "#4B5563",
  gold: "#6B7280",
  goldDark: "#374151",
};
const palette = (bw) => bw
  ? { burgundy: BW.burgundy, burgundyDark: BW.burgundyDark, burgundyLight: BW.burgundyLight, gold: BW.gold, goldDark: BW.goldDark }
  : { burgundy: BURGUNDY, burgundyDark: BURGUNDY_DARK, burgundyLight: BURGUNDY_LIGHT, gold: GOLD, goldDark: GOLD_DARK };

function Medalla({ x = 70, y = 205 }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      {/* Listón izquierdo */}
      <polygon points="-22,-70 -8,-70 -2,10 -22,-10" fill={BURGUNDY} />
      <polygon points="-22,-70 -15,-70 -12,-15 -22,-25" fill={BURGUNDY_DARK} />
      {/* Listón derecho */}
      <polygon points="22,-70 8,-70 2,10 22,-10" fill={BURGUNDY_LIGHT} />
      <polygon points="22,-70 15,-70 12,-15 22,-25" fill={BURGUNDY} />
      {/* Roseta base */}
      <g>
        {Array.from({ length: 12 }).map((_, i) => (
          <ellipse key={i} cx="0" cy="0" rx="6" ry="26" transform={`rotate(${i * 30})`} fill={i % 2 ? GOLD_DARK : GOLD} opacity="0.85" />
        ))}
      </g>
      {/* Medalla */}
      <circle cx="0" cy="0" r="24" fill={GOLD} stroke={GOLD_DARK} strokeWidth="2" />
      <circle cx="0" cy="0" r="19" fill="none" stroke={GOLD_DARK} strokeWidth="0.8" />
      <circle cx="0" cy="0" r="15" fill={BURGUNDY} />
      <text x="0" y="4" textAnchor="middle" fontFamily="Georgia, serif" fontSize="14" fontWeight="bold" fill={GOLD}>★</text>
    </g>
  );
}

export default function DiplomaPrint() {
  const [sp] = useSearchParams();
  const plantelId = sp.get("plantel_id");
  const grupoId = sp.get("grupo_id");
  const alumnoId = sp.get("alumno_id");
  const lugarPos = parseInt(sp.get("lugar") || "1", 10);
  const periodo = parseInt(sp.get("periodo") || "1", 10);
  const fecha = sp.get("fecha") || new Date().toISOString().slice(0, 10);
  const ciudad = sp.get("ciudad") || "Cd. y Pto. de Tampico, Tam.";
  const [plantel, setPlantel] = useState(null);
  const [grupo, setGrupo] = useState(null);
  const [alumno, setAlumno] = useState(null);
  const [director, setDirector] = useState(null);
  const [docente, setDocente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bw, setBw] = useState(false);
  const C = palette(bw);

  useEffect(() => {
    (async () => {
      try {
        const pls = (await api.get("/planteles")).data;
        setPlantel(pls.find((p) => p.id === plantelId) || null);
        if (grupoId) {
          const g = (await api.get(`/grupos/${grupoId}`)).data;
          setGrupo(g);
          const als = (await api.get("/alumnos", { params: { grupo_id: grupoId } })).data;
          setAlumno(als.find((a) => a.id === alumnoId) || null);
          const staff = (await api.get(`/planteles/${plantelId}/staff-list`)).data;
          setDirector(staff.director || null);
          setDocente((staff.docentes || []).find((d) => d.id === g.docente_id) || null);
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [plantelId, grupoId, alumnoId]);

  const d = useMemo(() => {
    const dd = new Date(fecha + "T12:00:00");
    return { day: String(dd.getDate()).padStart(2, "0"), mon: MESES[dd.getMonth()], year: dd.getFullYear() };
  }, [fecha]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Cargando…</div>;
  if (!alumno) return <div className="min-h-screen flex items-center justify-center text-[#9F2241]">Alumno no encontrado</div>;

  const alumnoName = titleCase(nombreCompleto(alumno));
  const dirName = director ? titleCase(nombreCompleto(director)) : "________________________________";
  const docName = docente ? titleCase(nombreCompleto(docente)) : "________________________________";
  const dirPrefix = prefijoDocenteFromCurp(director?.curp);
  const docPrefix = prefijoDocenteFromCurp(docente?.curp);
  const dirCargo = cargoConGeneroFromCurp(director?.curp, "Director", "Directora", "Director(a)");

  return (
    <div className="min-h-screen bg-[#EAECEE] print:bg-white p-6 print:p-0">
      <div className="mb-4 flex justify-end gap-2 print:hidden max-w-[1100px] mx-auto">
        <button onClick={() => setBw(!bw)} className="px-3 py-1.5 rounded border border-[#235B4E] text-[#235B4E] text-sm" data-testid="btn-toggle-bw-dip">
          {bw ? "Color" : "Blanco y negro"}
        </button>
        <button onClick={() => window.print()} className="px-3 py-1.5 rounded bg-[#235B4E] text-white text-sm" data-testid="btn-print-dip">Imprimir / PDF</button>
      </div>
      <div className="hoja bg-white mx-auto shadow print:shadow-none relative overflow-hidden" style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: "#1F2937" }}>
        {/* SVG decorativo de fondo — curvas y medalla */}
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 279 216" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: bw ? "grayscale(1) contrast(1.15)" : "none" }}>
          <defs>
            <linearGradient id="curv1-d" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={BURGUNDY_LIGHT} />
              <stop offset="100%" stopColor={BURGUNDY_DARK} />
            </linearGradient>
            <linearGradient id="curv2-d" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={BURGUNDY} />
              <stop offset="100%" stopColor={BURGUNDY_DARK} />
            </linearGradient>
            <pattern id="rays-d" x="0" y="0" width="216" height="216" patternUnits="userSpaceOnUse">
              <g transform="translate(108 108)">
                {Array.from({ length: 60 }).map((_, i) => (
                  <line key={i} x1="0" y1="0" x2="200" y2="0" stroke="#E7DDC7" strokeWidth="0.3" transform={`rotate(${i * 6})`} />
                ))}
              </g>
            </pattern>
          </defs>
          {/* Fondo con rayos sutiles */}
          <rect x="0" y="0" width="279" height="216" fill="url(#rays-d)" opacity="0.35" />
          {/* Forma superior burgundy — mayormente plana en la parte derecha, dip en la izquierda */}
          <path d="M 0,0 L 279,0 L 279,8 C 220,8 160,10 100,14 C 60,20 25,55 0,60 Z" fill="url(#curv1-d)" />
          <path d="M 0,0 L 200,0 C 150,10 100,15 55,22 C 30,45 10,54 0,52 Z" fill={BURGUNDY_DARK} opacity="0.85" />
          {/* Forma inferior izquierda (medalla) */}
          <path d="M 0,120 C 40,110 55,140 60,175 C 62,195 45,210 20,216 L 0,216 Z" fill="url(#curv2-d)" />
          <path d="M 0,140 C 30,132 45,155 50,180 C 52,200 38,212 15,216 L 0,216 Z" fill={BURGUNDY_DARK} />
          {/* Bordes dorados */}
          <path d="M 0,60 C 25,55 60,20 100,14 C 160,10 220,8 279,8" fill="none" stroke={GOLD} strokeWidth="0.6" />
          <path d="M 0,120 C 40,110 55,140 60,175 C 62,195 45,210 20,216" fill="none" stroke={GOLD} strokeWidth="0.6" />
          {/* Marco dorado interior */}
          <rect x="8" y="8" width="263" height="200" fill="none" stroke={GOLD} strokeWidth="0.4" />
          <rect x="10" y="10" width="259" height="196" fill="none" stroke={GOLD} strokeWidth="0.15" />
          {/* Medalla */}
          <Medalla x={62} y={168} />
        </svg>

        {/* Contenido — lado derecho */}
        <div className="relative h-full flex" style={{ padding: "24mm 18mm 14mm 18mm" }}>
          {/* Espacio izquierdo reservado para curvas/medalla */}
          <div style={{ width: "82mm" }} />
          <div className="flex-1 flex flex-col" style={{ paddingLeft: "6mm" }}>
            {/* Logo + Encabezado escuela — 50% más grande */}
            <div className="flex items-center gap-6">
              <img src={plantel?.logo_url || APP_LOGO} className="object-contain" style={{ width: "34mm", height: "34mm" }} alt="Logo" crossOrigin="anonymous" />
              <div className="flex-1 text-center">
                <div className="text-[22pt] font-bold uppercase leading-tight" style={{ fontFamily: "'Montserrat', sans-serif", color: C.burgundyDark, letterSpacing: "0.04em" }}>
                  {plantel?.nombre || "—"}
                </div>
                <div className="text-[11pt] mt-1" style={{ color: C.burgundy }}>
                  C.C.T. <span className="font-mono">{plantel?.cct || "—"}</span>
                  <span className="inline-block" style={{ width: "12mm" }} />
                  TURNO <strong>{(plantel?.turno || "—").toUpperCase()}</strong>
                </div>
              </div>
            </div>

            {/* Bloque central — separado del header */}
            <div className="flex-1 flex flex-col justify-center" style={{ paddingTop: "14mm" }}>
              {/* Título DIPLOMA */}
              <div>
                <div className="text-[9pt] uppercase tracking-[0.5em]" style={{ color: C.burgundy }}>El presente</div>
                <div className="text-[48pt] font-bold leading-none mt-1" style={{ fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif", color: C.burgundyDark, letterSpacing: "0.02em" }}>
                  DIPLOMA
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <div style={{ width: "8mm", height: "1px", background: C.gold }} />
                  <div className="text-[9pt] uppercase tracking-[0.4em]" style={{ color: C.goldDark }}>Se otorga con orgullo a</div>
                  <div style={{ flex: 1, height: "1px", background: C.gold, opacity: 0.4 }} />
                </div>
              </div>

              {/* Nombre alumno */}
              <div className="mt-6">
                <div className="text-[36pt] italic leading-tight" style={{ fontFamily: "'Great Vibes', 'Allura', 'Pinyon Script', cursive", color: C.burgundyDark }}>
                  {alumnoName}
                </div>
                <div style={{ borderBottom: `0.8px solid ${C.gold}`, marginTop: "3mm", width: "90%" }} />
              </div>

              {/* Cuerpo — centrado */}
              <div className="mt-6 text-[11.5pt] leading-[1.7] text-center" style={{ color: "#374151", maxWidth: "155mm" }}>
                Por haber obtenido el <strong style={{ color: C.burgundy }}>{LUGAR_TXT[lugarPos] || "PRIMER LUGAR"}</strong> en aprovechamiento académico durante el{" "}
                <strong style={{ color: C.burgundy }}>{PERIODO_TXT[periodo] || "PRIMER"} PERIODO</strong> del{" "}
                <strong style={{ color: C.burgundy }}>CICLO ESCOLAR {grupo?.ciclo || "—"}</strong>, demostrando esfuerzo, dedicación y excelencia.
              </div>

              <div className="mt-4 text-[10pt] text-center" style={{ color: "#4B5563" }}>
                <strong>{ciudad}</strong>, a los <strong>{d.day}</strong> días del mes de <strong>{d.mon}</strong> de <strong>{d.year}</strong>.
              </div>
            </div>

            {/* Firmas */}
            <div className="flex justify-between gap-8" style={{ paddingRight: "4mm" }}>
              <div className="flex-1 text-center">
                <div style={{ borderTop: `1px solid ${C.burgundyDark}`, paddingTop: "1.5mm" }}>
                  <div className="text-[9.5pt] font-bold uppercase" style={{ color: C.burgundyDark, fontFamily: "'Montserrat', sans-serif" }}>{dirPrefix} {dirName}</div>
                  <div className="text-[7.5pt] uppercase tracking-[0.25em]" style={{ color: C.goldDark }}>{dirCargo} de la Escuela</div>
                </div>
              </div>
              <div className="flex-1 text-center">
                <div style={{ borderTop: `1px solid ${C.burgundyDark}`, paddingTop: "1.5mm" }}>
                  <div className="text-[9.5pt] font-bold uppercase" style={{ color: C.burgundyDark, fontFamily: "'Montserrat', sans-serif" }}>{docPrefix} {docName}</div>
                  <div className="text-[7.5pt] uppercase tracking-[0.25em]" style={{ color: C.goldDark }}>Docente del Grupo</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&family=Playfair+Display:wght@700;900&family=Montserrat:wght@600;700&display=swap');
        @page { size: Letter landscape; margin: 0; }
        .hoja { width: 279mm; height: 216mm; box-sizing: border-box; }
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: white; }
          .hoja { box-shadow: none !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>
    </div>
  );
}
